<template>
  <div class="order-status-modal">
    <div class="order-status-modal__grid">
      <!-- DEBUG: показываем информацию о загрузке -->
      <div v-if="isLoadingDetails" class="loading-section">
        <p>Загрузка деталей заказа...</p>
      </div>

      <!-- Показываем контент только когда данные загружены -->
      <div v-if="!order && !isLoadingDetails" class="loading-section">
        <p>⚠️ Данные заказа недоступны</p>
      </div>

      <!-- Заголовок и детали заказа -->
      <OrderHeader v-if="order" :order="order" />

      <!-- Статус и прогресс -->
      <OrderProgress v-if="order" :order="order" />

      <!-- Уведомления о статусе заказа -->
      <div v-if="!pushNotificationsEnabled" class="order-notifications">
        <div class="setting-item">
          <div class="setting-info">
            <span class="setting-label">Уведомления о готовности</span>
            <span class="setting-description">Получайте push-уведомления о статусах готовности заказа</span>
          </div>
          <UiKitSwitch
            :model-value="pushNotificationsEnabled"
            color="warning"
            :disabled="pushToggleLoading"
            class="setting-toggle"
            @update:model-value="handlePushToggle"
          />
        </div>
      </div>

      <!-- Оценка заказа -->
      <OrderRating
        v-if="order && orderIdForRating"
        :order="order"
        :order-id="orderIdForRating as any"
        :user-id="userId"
        @rating-sent="handleRatingSent"
        @rating-error="handleRatingError"
      />

      <!-- Кнопка "Оплатить" для неоплаченных заказов -->
      <RetryPaymentSection
        v-if="order"
        :order="order"
        :can-retry-payment="canRetryPayment"
        :is-retrying-payment="isRetryingPayment"
        :is-initiating-payment="isInitiatingPayment"
        :formatted-time-remaining="formattedTimeRemaining"
        :payment-url="paymentUrl"
        @retry-payment="handleRetryPayment"
        @create-new-order="handleCreateNewOrder"
      />

      <UiKitButton
        v-if="order"
        color="primary"
        variant="flat"
        size="lg"
        radius="lg"
        full-width
        @click="openSupportChat"
      >
        <template #leading>
          <UIcon name="i-heroicons-chat-bubble-left-right" class="h-5 w-5 shrink-0" />
        </template>
        Проблемы с заказом? Напишите нам
      </UiKitButton>

      <!-- Повторить заказ: переносит позиции в корзину -->
      <RepeatOrderButton v-if="order" :order="order" />

      <!-- Состав заказа -->
      <OrderItems
        v-if="order"
        :order="order"
        :addons="addons"
        :excludes="excludes"
      />

      <!-- Суммы -->
      <OrderSummary v-if="order" :order="order" />

      <!-- Информация о времени и адресе -->
      <OrderFooter v-if="order" :order="order" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, ref } from 'vue'
import { consola } from 'consola'
import { Capacitor } from '@capacitor/core'
import { Dialog } from '@capacitor/dialog'
import { useOrderModal } from '../model'
import RepeatOrderButton from './RepeatOrderButton.vue'
import { isOrderPaidOrCompleted } from '@entities/order'
import { usePushNotificationSettings } from '~/features/notifications/@x/order-history'
import { useAlertStore, useModalStore } from '@shared/model'

// Импорты компонентов
import OrderHeader from '~/features/order-history/ui/OrderHeader.vue'
import OrderProgress from '~/features/order-history/ui/OrderProgress.vue'
import OrderRating from '~/features/order-history/ui/OrderRating.vue'
import RetryPaymentSection from '~/features/order-history/ui/RetryPaymentSection.vue'
import OrderItems from '~/features/order-history/ui/OrderItems.vue'
import OrderSummary from '~/features/order-history/ui/OrderSummary.vue'
import OrderFooter from '~/features/order-history/ui/OrderFooter.vue'

const logger = consola.withTag('ModalOrderDetails')

// Используем composable для всей логики модального окна
const {
  // State
  order,
  currentOrderId,
  paymentUrl,
  userId,
  addons,
  excludes,
  canRetryPayment,
  formattedTimeRemaining,
  isRetryingPayment,
  isLoadingDetails,
  isInitiatingPayment,

  // Actions
  handleRetryPayment,
  handleCreateNewOrder,
  clearUnpaidOrder,
  clearCompletedOrderStorage,
  initialize,
  cleanup,
} = useOrderModal()

// Push notifications
const {
  isPushNotificationsEnabled,
  enablePushNotifications,
  disablePushNotifications,
  getSystemPermissionStatus,
  requestSystemPermission,
  openSystemSettings,
} = usePushNotificationSettings()

const pushNotificationsEnabled = ref(false)
const pushToggleLoading = ref(false)

// Computed для orderId совместимого с OrderRating
const orderIdForRating = computed(() => {
  const id = currentOrderId.value
  return id !== null && id !== undefined ? id : null
})

/**
 * Обрабатывает успешную отправку рейтинга
 */
function handleRatingSent(): void {
  logger.debug('Rating sent successfully')
  // После отправки рейтинга очищаем только localStorage, но сохраняем данные в store
  clearCompletedOrderStorage()
}

/**
 * Обрабатывает ошибку отправки рейтинга
 */
function handleRatingError(error: string): void {
  logger.error('Rating error:', error)
  const alertStore = useAlertStore()
  alertStore.error(error)
}

// Чат поддержки с автоприкреплением этого заказа (раньше — внешняя ссылка
// director_manager); модалка закрывается, чтобы не висеть под страницей чата
function openSupportChat(): void {
  const orderId = currentOrderId.value
  useModalStore().close()
  navigateTo(orderId ? `/support?create=1&orderId=${orderId}` : '/support')
}

// Обработчик переключения push уведомлений
const handlePushToggle = async (enabled: boolean) => {
  pushToggleLoading.value = true

  try {
    if (enabled) {
      const status = await getSystemPermissionStatus()

      if (status === 'granted') {
        pushNotificationsEnabled.value = await enablePushNotifications()
        return
      }

      if (status === 'prompt') {
        const requestResult = await requestSystemPermission()
        if (requestResult === 'granted') {
          pushNotificationsEnabled.value = await enablePushNotifications()
          return
        }
        // Пользователь отказал — оставляем выключенным
        pushNotificationsEnabled.value = false
        return
      }

      // denied — показываем нативный диалог iOS
      pushNotificationsEnabled.value = false
      if (Capacitor.isNativePlatform()) {
        const { value } = await Dialog.confirm({
          title: 'Включите уведомления в настройках',
          message: 'Напомним о статусе заказа и бонусах. Расскажем о новеньком в меню и акциях',
          okButtonTitle: 'Настройки',
          cancelButtonTitle: 'Отменить',
        })
        if (value) {
          await openSystemSettings()
        }
      } else {
        // Web fallback
        const goToSettings = confirm('Уведомления выключены в настройках. Открыть настройки, чтобы включить?')
        if (goToSettings) {
          await openSystemSettings()
        }
      }
      return
    }

    // Выключение (сохраняем токен, только флаг)
    const success = await disablePushNotifications()
    pushNotificationsEnabled.value = !success
  } catch (error) {
    logger.error('Error toggling push notifications:', error)
    // Возвращаем в исходное состояние при ошибке
    pushNotificationsEnabled.value = !enabled
  } finally {
    pushToggleLoading.value = false
  }
}

// Инициализация при монтировании
onMounted(async () => {
  await initialize()
  // Инициализация состояния push уведомлений
  pushNotificationsEnabled.value = await isPushNotificationsEnabled()
})

// Очистка при размонтировании
onUnmounted(() => {
  cleanup()

  // При закрытии модального окна, если заказ завершен, очищаем все данные
  if (order && isOrderPaidOrCompleted(order) && order.status === 'delivered') {
    logger.debug('Modal closing, order is fully completed - clearing all payment data')
    clearUnpaidOrder() // Полная очистка только для полностью завершенных заказов
  }
})
</script>

<style scoped lang="scss">
.order-status-modal {
  @apply bg-hui-background text-hui-foreground;
  @apply px-4 pb-6 pt-16 md:px-10 md:py-12;
  @apply max-md:min-h-[calc(100dvh-0.625rem)];

  &__grid {
    @apply grid w-full max-w-xl mx-auto gap-4 content-start;
  }
}

.loading-section {
  @apply text-center p-4 text-hui-default-500;
}

.order-notifications {
  @apply bg-hui-content2 rounded-hui-lg p-4 w-full;
}

.setting-item {
  @apply flex items-center justify-between gap-4;
}

.setting-info {
  @apply flex flex-col gap-1 flex-1;
}

.setting-label {
  @apply font-semibold text-hui-foreground;
}

.setting-description {
  @apply text-sm text-hui-default-500;
}

.setting-toggle {
  @apply shrink-0;
}
</style>